import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useCallback } from "react";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { accountAtom } from "shared/lib/account";

import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SubscriptionEntryPointImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "scmp-app/lib/rosetta";

import {
  ButtonWrapper,
  Container,
  Content,
  HighlightedContent,
  LoginMessage,
  LoginText,
  LoginWrapper,
  StyledButton,
  StyledLogo,
  Wrapper,
} from "./styles";

const Component: FunctionComponent = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { isLoggedIn } = useAtomValue(accountAtom);
  const hasScmpPlusUpgradeOptions = asyncRosettaState?.result?.user?.hasScmpPlusUpgradeOptions;
  const isEligibleForSCMPPlusFreeTrialSubscribeOffer =
    asyncRosettaState?.result?.user?.isEligibleForSCMPPlusFreeTrialSubscribeOffer;

  const { openLoginDialog } = useLoginDialogStateHelper();

  const onClickHandler = () => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        call_to_action: callToAction,
        trigger_point: "hard_link",
      },
      subcategory: "entry_point",
    });
  };

  const handleCTAClick = () => {
    onClickHandler();
    window.open("https://subscribe.scmp.com/plus", "_blank");
  };

  const handleLoginClick = () => {
    onClickHandler();
    openLoginDialog({
      application: "scmp-plus",
      destination: window.location.href,
      ga4CustomParameter: {
        product: "plus",
        trigger_point: "hard_link",
      },
    });
  };

  const ctaLabel = (() => {
    if (!isLoggedIn) return "TRY FOR FREE or UPGRADE";
    if (hasScmpPlusUpgradeOptions) return "UPGRADE";
    if (isEligibleForSCMPPlusFreeTrialSubscribeOffer) return "TRY FOR FREE";
    return "SUBSCRIBE";
  })();

  const callToAction = (() => {
    if (!isLoggedIn) return "plus trial/upgrade";
    if (hasScmpPlusUpgradeOptions) return "upgrade";
    if (isEligibleForSCMPPlusFreeTrialSubscribeOffer) return "plus trial";
    return "subscribe";
  })();

  const impressionGA4Event = useCallback(
    (): SubscriptionEntryPointImpressionEvent => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: callToAction,
        trigger_point: "hard_link",
      },
      subcategory: "entry_point",
    }),
    [callToAction],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<SubscriptionEntryPointImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return (
    <Container ref={captureTrackImpressionEventTargetElement}>
      <StyledLogo />
      <Wrapper>
        <Content>
          SCMP Plus is a new premium news platform that gives you an
          <HighlightedContent>
            {" "}
            all-inclusive edge to stay ahead on China news.
          </HighlightedContent>{" "}
          To access our exclusive content you’ll need to subscribe.
        </Content>
        <ButtonWrapper>
          <StyledButton onClick={handleCTAClick}>{ctaLabel}</StyledButton>
          <LoginWrapper>
            <LoginMessage>Already a subscriber?</LoginMessage>
            <LoginText onClick={handleLoginClick}>LOG IN</LoginText>
          </LoginWrapper>
        </ButtonWrapper>
      </Wrapper>
    </Container>
  );
};

Component.displayName = "InlinePlusWidgetNonSubscriber";

export const InlinePlusWidgetNonSubscriber = styled(Component)``;
